import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, Refresh, Receipt } from '@mui/icons-material';

import { useFetchHbciAccountTransactions } from '../hooks/reactQuerys/HbciAccountQueries';
import {
  IHbciRefreshTanRequired,
  IHbciTransaction,
  IHbciTransactionsResult,
} from '../Interfaces/IHbciAccount';

interface Props {
  open: boolean;
  idHbciAccount: number;
  iban?: string;
  bankName?: string;
  onClose: () => void;
  onTanChallenge?: (tan: IHbciRefreshTanRequired) => void;
}

const today = () => new Date().toISOString().slice(0, 10);
const minus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

const formatAmount = (val: number | null | undefined, ccy?: string | null) => {
  if (val === null || val === undefined) return '–';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: ccy || 'EUR',
    }).format(val);
  } catch {
    return String(val);
  }
};

const formatDate = (val?: string | null) => {
  if (!val) return '–';
  try {
    return new Date(val).toLocaleDateString('de-DE');
  } catch {
    return val;
  }
};

/**
 * Dialog zum Einsehen der Kontobewegungen eines Notaranderkontos.
 * Holt die Umsaetze on-demand per HKKAZ — nicht persistiert.
 */
export const HbciTransactionsDialog: React.FC<Props> = ({
  open,
  idHbciAccount,
  iban,
  bankName,
  onClose,
  onTanChallenge,
}) => {
  const { fetchHbciAccountTransactions, isFetchingHbciAccountTransactions } =
    useFetchHbciAccountTransactions();

  const [fromDate, setFromDate] = useState(minus30());
  const [toDate, setToDate] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IHbciTransactionsResult | null>(null);

  const run = async () => {
    setError(null);
    try {
      const r = await fetchHbciAccountTransactions({
        idHbciAccount,
        fromDate,
        toDate,
      });
      if ((r as IHbciRefreshTanRequired).tanRequired) {
        onTanChallenge?.(r as IHbciRefreshTanRequired);
        return;
      }
      setResult(r as IHbciTransactionsResult);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Buchungen konnten nicht abgerufen werden.'
      );
    }
  };

  // Beim Oeffnen direkt einmal abrufen. ``run`` bewusst NICHT in der
  // Dep-Liste — ``fetchHbciAccountTransactions`` von useMutation wechselt
  // bei jedem Render die Referenz und wuerde sonst Endlos-Schleifen
  // ausloesen.
  const runRef = React.useRef(run);
  runRef.current = run;
  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      void runRef.current();
    }
  }, [open, idHbciAccount]);

  // Neueste Buchungen zuerst — die Bank liefert nicht garantiert sortiert.
  // Sortierung nach Buchungsdatum (Date), fallback Valuta, fallback Index.
  const transactions: IHbciTransaction[] = React.useMemo(() => {
    const list = [...(result?.transactions ?? [])];
    list.sort((a, b) => {
      const aKey = a.Date || a.Valuta || '';
      const bKey = b.Date || b.Valuta || '';
      if (aKey === bKey) return 0;
      return aKey < bKey ? 1 : -1;
    });
    return list;
  }, [result]);
  const sum = transactions.reduce((acc, t) => acc + (t.Amount ?? 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Receipt color='primary' />
          <Box sx={{ flex: 1 }}>
            <Typography variant='h6' component='div'>
              Kontobewegungen
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {iban}
              {bankName ? ` · ${bankName}` : ''}
            </Typography>
          </Box>
          <Tooltip title='Erneut abrufen'>
            <span>
              <IconButton
                onClick={run}
                disabled={isFetchingHbciAccountTransactions}
              >
                {isFetchingHbciAccountTransactions ? (
                  <CircularProgress size={20} />
                ) : (
                  <Refresh />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
          <TextField
            label='Von'
            type='date'
            size='small'
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label='Bis'
            type='date'
            size='small'
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant='outlined'
            onClick={run}
            disabled={isFetchingHbciAccountTransactions}
            startIcon={
              isFetchingHbciAccountTransactions ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh />
              )
            }
          >
            Abrufen
          </Button>
        </Stack>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result && transactions.length === 0 && !error && (
          <Alert severity='info'>Keine Buchungen im gewählten Zeitraum.</Alert>
        )}

        {transactions.length > 0 && (
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Valuta</TableCell>
                <TableCell>Buchungstext</TableCell>
                <TableCell>Auftraggeber / Empfänger</TableCell>
                <TableCell>Verwendungszweck</TableCell>
                <TableCell align='right'>Betrag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((t, idx) => (
                <TableRow key={`tx-${idx}`} hover>
                  <TableCell>{formatDate(t.Date)}</TableCell>
                  <TableCell>{formatDate(t.Valuta)}</TableCell>
                  <TableCell>{t.PostingText || '–'}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant='body2'>
                        {t.Applicant || '–'}
                      </Typography>
                      {t.ApplicantIBAN && (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          fontFamily='monospace'
                        >
                          {t.ApplicantIBAN}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                    {t.Purpose || '–'}
                  </TableCell>
                  <TableCell align='right'>
                    <Typography
                      variant='body2'
                      color={
                        (t.Amount ?? 0) < 0 ? 'error.main' : 'success.main'
                      }
                    >
                      {formatAmount(t.Amount, t.Currency)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5} align='right'>
                  <strong>Summe Zeitraum</strong>
                </TableCell>
                <TableCell align='right'>
                  <strong>
                    {formatAmount(sum, transactions[0]?.Currency)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
